import usePim from '~/composables/pim_json_loader'
import useI18nExtended from '~/composables/usei18nExtended'
import {getPages} from '../config/helper'

const ccrsTranslateFilters = new Set(['brand', 'material1ShoreHardness', 'productDesign'])

/**
 * A composable for working with PIM data
 * @returns Functions for working with PIM data
 */
export const usePimHelper = (i18nInput?: any) => {
  const i18n = i18nInput ?? useI18nExtended()
  /**
   * Maps category filter data from PIM format to application format
   * @param f The filter object from PIM
   * @param isCore Whether this is a core filter
   * @returns The mapped filter object
   */
  const mapCategoryFilter = (f: any, isCore: boolean, isCcrs: boolean = false) => {
    const valueType = typeof f.type === 'string' && f.type.length > 0
      ? f.type.charAt(0).toUpperCase() + f.type.slice(1)
      : f.type
    return {
      IndexPath: f.indexPath,
      Attribute: {
        IndexPath: f.indexPath,
        IsFilterable: true,
        TechnicalName: f.technicalName,
        UnitOfMeasure: f.unitOfMeasure,
        ValueType: valueType,
        CustomTranslatePrefix: isCcrs && f.indexPath === 'productDesign' ? 'categories' : undefined
      },
      IsCoreFilter: isCore,
      IsFilterable: true,
      Label: (isCcrs || ccrsTranslateFilters.has(f.technicalName)) ? i18n.tv(f.displayName || f.shortName, 'attributes') : f.displayName,
      ShortLabel: f.shortName,
      IsActive: true,
      Value: f.value
    }
  }

  /**
   * Maps category table column data from PIM format to application format
   * @param col The column object from PIM
   * @param index The index of the column
   * @param isFixed Whether this is a fixed column
   * @returns The mapped column object
   */
  const mapCategoryTableColumn = (col: any, index: number, isFixed: boolean = false) => {
    return {
      IndexPath: col.indexPath,
      ShowInProductTable: true,
      ProductTableSortOrder: index,
      Label: col.displayName,
      ShortLabel: col.shortName,
      Attribute: {
        IndexPath: col.indexPath,
        TechnicalName: col.technicalName,
        IsFixedColumn: isFixed,
        ValueType: col.type,
        UnitOfMeasure: col.unitOfMeasure,
        IsSortable: true
      }
    }
  }

  /**
   * Maps category table columns from PIM format to application format
   * @param categoryJson The category JSON data
   * @returns Array of mapped column objects
   */
  const mapCategoryTableColumns = (categoryJson: any) => {
    const columns: any[] = []
    // Map fixed columns
    if (categoryJson?.categoryAttributes_table_fixed) {
      const fixedColumns = categoryJson.categoryAttributes_table_fixed.map((col: any, index: number) =>
        mapCategoryTableColumn(col, index, true)
      )
      columns.push(...fixedColumns)
    }

    // Map regular columns
    if (categoryJson?.categoryAttributes_table) {
      const regularColumns = categoryJson.categoryAttributes_table.map((col: any, index: number) =>
        mapCategoryTableColumn(col, index, false)
      )
      columns.push(...regularColumns)
    }
    return columns
  }

  const mapCcrsTableColumnsFromFilters = (ccrsFilters: any, ccrsType: 'product' | 'design' = 'design') => {
    const columns: any[] = []
    const fixedColumnNames = new Set(['sku', 'productDesign', 'material1name'])
    const fixedColumns = ccrsFilters.filter((f: any) => fixedColumnNames.has(f.shortName))
    const nonFixedColumns = ccrsFilters.filter((f: any) => !fixedColumnNames.has(f.shortName))

    // Ensure material1Name is always present in fixed columns for design searches
    if (ccrsType === 'design' && !fixedColumns.some((f: any) => f.indexPath === 'material1Name')) {
      fixedColumns.push({
        shortName: 'material1Name',
        indexPath: 'material1Name',
        displayName: i18n.tv('material', 'product'),
        type: 'string',
        unitOfMeasure: null
      })
    }

    // Map fixed columns
    if (fixedColumns.length > 0) {
      const fixedColumnsMapped = fixedColumns.map((col: any, index: number) => ({
        IndexPath: col.indexPath,
        ShowInProductTable: true,
        ProductTableSortOrder: index,
        Label: col.displayName,
        ShortLabel: col.displayNameShort,
        Attribute: {
          IndexPath: col.indexPath,
          TechnicalName: col.shortName,
          IsFixedColumn: true,
          ValueType: col.type,
          UnitOfMeasure: col.unitOfMeasure,
          IsSortable: true
        }
      }))
      columns.push(...fixedColumnsMapped)
    }

    // Map regular columns
    if (nonFixedColumns.length > 0) {
      const regularColumns = nonFixedColumns.map((col: any, index: number) => ({
        IndexPath: col.indexPath,
        ShowInProductTable: true,
        ProductTableSortOrder: index,
        Label: col.displayName,
        ShortLabel: col.displayNameShort,
        Attribute: {
          IndexPath: col.indexPath,
          TechnicalName: col.shortName,
          IsFixedColumn: false,
          ValueType: col.type,
          UnitOfMeasure: col.unitOfMeasure,
          IsSortable: true
        }
      }))
      columns.push(...regularColumns)
    }
    return columns
  }

  /**
   * Gets category filters from JSON data for a specific category
   * @param category The category to get filters for
   * @returns An array of category filters
   */
  const getCategoryFiltersFromJson = async (category: string) => {
    if (!category) {
      return []
    }

    const route = useRoute()
    const categoryJson = await loadProductCategoryWithPim(category, route)
    if (!categoryJson) {
      return []
    }

    return [
      ...(categoryJson?.categoryAttributes_filter_core?.map(f => mapCategoryFilter(f, true)) || []),
      ...(categoryJson?.categoryAttributes_filter?.map(f => mapCategoryFilter(f, false)) || [])
    ]
  }

  const loadProductCategoryWithPim = async (technicalName: string, route: any) => {
    try {
      let basePath = ''
      const segments = route.path.split('/').slice(0, 3)
      basePath = segments.length ? segments.join('/') : ''
      const pages = getPages() as Record<string, any>
      const currentLocale = i18n.locale.value as string
      let categorie = pages[currentLocale]?.categories || ''
      basePath = basePath + '/' + categorie

      let category = await usePim(`v1${basePath}_technical/${technicalName.toLowerCase()}.json`)

      let parsedCategoryJson = typeof category === 'string' ? JSON.parse(category) : category

      let loopCount = 0
      while (parsedCategoryJson?.redirect && !parsedCategoryJson?.redirect.includes('#name?') && !parsedCategoryJson?.redirect.includes('undefined') && loopCount < 10) {
        const categoryFileName = `v1${parsedCategoryJson.redirect}.json`
        category = await usePim(categoryFileName)
        parsedCategoryJson = typeof category === 'string' ? JSON.parse(category) : category
        loopCount++
      }

      return category
    } catch (error) {
      return null
    }
  }

  const getPimImgUrl = (path: string) => {
    const config = useRuntimeConfig()
    return config.public.pimDataBaseUrl + '/' + path
  }

  const getPimImgUrlMedium = (image: any) => {
    if (image) {
      if (image?.sizes?.['easy-medium']?.imgUrl && !image?.imgURL.endsWith('.svg')) {
        return getPimImgUrl(image.sizes['easy-medium'].imgUrl)
      }
      return getPimImgUrl(image?.imgURL)
    } else {
      return '/img/no_image.png'
    }
  }

  const getPimImgUrlLarge = (image: any) => {
    if (image) {
      if (image?.sizes?.['easy-large']?.imgUrl && !image?.imgURL.endsWith('.svg')) {
        return getPimImgUrl(image.sizes['easy-large'].imgUrl)
      }
      return getPimImgUrl(image?.imgURL)
    } else {
      return '/img/no_image.png'
    }
  }

  return {
    mapCategoryFilter,
    mapCategoryTableColumn,
    mapCategoryTableColumns,
    getCategoryFiltersFromJson,
    loadProductCategoryWithPim,
    getPimImgUrl,
    getPimImgUrlMedium,
    getPimImgUrlLarge,
    mapCcrsTableColumnsFromFilters
  }
}

export default usePimHelper
